import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { resetPassword } from '../helper/auth';
import { auth } from '../helper/firebase';
import { toast } from 'react-hot-toast';
import AddressFields, { emptyAddress, composeAddress, addressToSubfields, splitZip } from './AddressFields';
import { fullStateName } from '../utils/stateNames';

// Split a legacy compiled address ("street, city, State Name ZIP, USA") into decomposed parts,
// used to seed the edit fields for older records that only have the compiled string. The state may
// be a multi-word full name ("New York"), so take the trailing 5-digit or ZIP+4 code from the END
// of the state/ZIP segment and treat everything before it as the state.
function parseCompiled(address) {
  if (!address) return { ...emptyAddress };
  const parts = address.split(',').map((s) => s.trim());
  const segment = parts[2] || '';
  const zipMatch = segment.match(/(\d{5}(?:-\d{4})?)\s*$/);
  const zip = zipMatch ? zipMatch[1] : '';
  const state = zip ? segment.slice(0, segment.length - zipMatch[0].length).trim() : segment;
  return { streetAddress: parts[0] || '', secondaryAddress: '', city: parts[1] || '', state: fullStateName(state), ZIPCode: zip, ZIPPlus4: splitZip(zip).plus4 };
}

// Build the decomposed address for a prefix ('home'|'shipping'). Prefer the decomposed
// QuickBase fields; fall back to the compiled string for older records that lack them.
function addressFromDj(dj, prefix) {
  const street1 = dj[`${prefix}_address_street_1`];
  const hasDecomposed = street1 || dj[`${prefix}_address_city`] || dj[`${prefix}_address_postal_code`];
  if (hasDecomposed) {
    const postal = dj[`${prefix}_address_postal_code`] || '';
    return {
      streetAddress: street1 || '',
      secondaryAddress: dj[`${prefix}_address_street_2`] || '',
      city: dj[`${prefix}_address_city`] || '',
      state: fullStateName(dj[`${prefix}_address_state_region`] || ''),
      ZIPCode: postal,
      ZIPPlus4: splitZip(postal).plus4,
    };
  }
  return parseCompiled(dj[`${prefix}_address`]);
}

// Reusable text input. Defined at MODULE scope (not inside ViewProfile) so its component
// identity is stable across re-renders. If it were nested, every ViewProfile re-render would
// create a new function identity, forcing React to remount these uncontrolled inputs and reset
// them to defaultValue — which silently wiped the DJ's typed edits whenever an error popup
// (duplicate/invalid) or any other edit-mode state change re-rendered the form.
const InputField = ({
  label,
  name,
  value,
  type = 'text',
  disabled = false,
  inputRef = null,
  required = false,
  placeholder = ''
}) => {
  // Handle changes for number inputs to prevent negative values
  const handleNumberInput = (e) => {
    if (type === 'number') {
      const value = Number(e.target.value);
      if (value < 0) {
        e.target.value = 0;
      }
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        defaultValue={value ?? ''}
        ref={inputRef}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        min={type === 'number' ? "0" : undefined}
        onInput={handleNumberInput}
        className={
          !disabled
            ? 'mt-1 block w-full rounded-md border-primary focus:border-primary focus:ring-primary bg-white shadow-sm sm:text-sm'
            : 'mt-1 block w-full rounded-md border-transparent bg-gray-50 shadow-sm sm:text-sm'
        }
      />
    </div>
  );
};

export default function ViewProfile({ dj = {}, setDj }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [showEmailPhoneError, setShowEmailPhoneError] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  // -- Address logic: USPS-verified, decomposed fields --
  const [sameAddress, setSameAddress] = useState(
    dj.shipping_address &&
    dj.home_address &&
    dj.shipping_address === dj.home_address
  );
  const [home, setHome] = useState(() => addressFromDj(dj, 'home'));
  const [shipping, setShipping] = useState(() => addressFromDj(dj, 'shipping'));
  const [homeVerified, setHomeVerified] = useState(false);
  const [shippingVerified, setShippingVerified] = useState(false);

  // Refs so Save can trigger USPS verification imperatively on each address field.
  const homeRef = useRef(null);
  const shippingRef = useRef(null);

  // Keep shipping mirrored to home (including its verified state) while "Same as home" is on.
  useEffect(() => {
    if (sameAddress) {
      setShipping(home);
      setShippingVerified(homeVerified);
    }
  }, [sameAddress, home, homeVerified]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Revert local addresses (and their verified state) to what's on the record.
    setHome(addressFromDj(dj, 'home'));
    setShipping(addressFromDj(dj, 'shipping'));
    setHomeVerified(false);
    setShippingVerified(false);
    setSameAddress(
      dj.shipping_address &&
      dj.home_address &&
      dj.shipping_address === dj.home_address
    );
  };

  const handleSubmit = async () => {
    const form = document.getElementById('djForm');
    if (!form) return;

    // Trigger HTML5 validation
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Collect form data
    const formData = new FormData(form);

    const cleanPhone = formData.get('phone_number').replace(/\D/g, '');
    const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/
    const phoneRegex = /^\d{10}$/; 
    if (!emailRegex.test(formData.get('email_address')) || !phoneRegex.test(cleanPhone)) {
      setShowEmailPhoneError(true);
      return;
    }
    // Verify addresses on save (shipping is exempt when it mirrors home). verify() returns the
    // standardized (canonical) address when confirmed, or false when it surfaces a correction
    // suggestion/error inline — in which case we halt and let the DJ resolve, then re-save. We
    // persist the returned standardized address so QuickBase stores the canonical form (matching
    // casing/ZIP+4), not the DJ's raw typing.
    let homeAddr = home;
    if (!homeVerified) {
      const r = homeRef.current && await homeRef.current.verify();
      if (!r) return;
      homeAddr = r;
    }
    let shipAddr;
    if (sameAddress) {
      shipAddr = homeAddr;
    } else {
      shipAddr = shipping;
      if (!shippingVerified) {
        const r = shippingRef.current && await shippingRef.current.verify();
        if (!r) return;
        shipAddr = r;
      }
    }

    const updatedDj = {
      ...dj,
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      stage_name: formData.get('stage_name'),
      email_address: formData.get('email_address'),
      phone_number: formData.get('phone_number'),
      website: formData.get('website'),
      // Subfields are the source of truth; the backend rolls them up into the compiled fields.
      // We also set the compiled strings locally (same format) so view mode reflects the change
      // immediately without a page refresh — the backend re-derives them from the subfields.
      home_address: composeAddress(homeAddr),
      shipping_address: composeAddress(shipAddr),
      ...addressToSubfields(homeAddr, 'home'),
      ...addressToSubfields(shipAddr, 'shipping'),
      instagram_handle: formData.get('instagram_handle'),
      instagram_followers: formData.get('instagram_followers'),
      tiktok_handle: formData.get('tiktok_handle'),
      tiktok_followers: formData.get('tiktok_followers'),
      facebook_handle: formData.get('facebook_handle'),
      facebook_followers: formData.get('facebook_followers'),
      twitter_handle: formData.get('twitter_handle'),
      twitter_followers: formData.get('twitter_followers'),
      twitch_handle: formData.get('twitch_handle'),
      twitch_followers: formData.get('twitch_followers'),
      has_org_connection: dj.has_org_connection || "No",
      org_connection_details: dj.org_connection_details || "",
    };

    // Additional check for duplicates in DB
    try {
      let existingUser = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/getDJs?phone=${updatedDj.phone_number}&email=${updatedDj.email_address}&rid=${dj.rid}`
      );
      if (existingUser.data.length > 0) {
        setShowDuplicateError(true);
        return;
      }

      // Save to DB
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/createDj`,
        updatedDj
      );
      console.log('Response from server:', res.data);

      // Update local state
      setDj(updatedDj);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  // Popup for duplicate phone/email
  const DuplicateErrorPopup = () => (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="duplicate-error-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="sm:flex sm:items-start">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                className="text-lg font-semibold leading-6 text-gray-900"
                id="duplicate-error-title"
              >
                Duplicate Information Detected
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  We already have a DJ registered with that phone number or email
                  address. Please try again using different contact information.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => setShowDuplicateError(false)}
              className="mt-3 inline-flex justify-center w-full px-4 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm 
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
const PhoneEmailErrorPopup = () => (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="duplicate-error-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="sm:flex sm:items-start">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                className="text-lg font-semibold leading-6 text-gray-900"
                id="duplicate-error-title"
              >
                Invalid Information Detected
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Please enter a valid 10-digit phone number and a valid email address.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => setShowEmailPhoneError(false)}
              className="mt-3 inline-flex justify-center w-full px-4 py-2 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm 
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      {showDuplicateError && <DuplicateErrorPopup />}
      {showEmailPhoneError && <PhoneEmailErrorPopup />}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl transform transition-all">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">Password Reset Email Sent!</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  We've sent a password reset link to:
                  <span className="block font-medium text-gray-900 mt-1">{auth.currentUser?.email}</span>
                </p>
                <p className="text-sm text-gray-500 mt-4">
                  Please check your inbox and follow the instructions to change your password.
                </p>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordResetModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Got it, thanks!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Mode Banner */}
      {isEditing && (
        <div className="bg-primary text-white px-4 py-3 mb-6 rounded-lg shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <p className="font-medium text-center sm:text-left">
              Edit Mode - Make your changes below
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleCancel}
                className="w-full sm:w-auto px-4 py-2 bg-white text-primary rounded hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="w-full sm:w-auto px-4 py-2 bg-white text-primary rounded hover:bg-gray-100 text-sm font-medium"
              >
                Save All Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image and Name */}
      <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
          <div className="flex flex-col items-center sm:items-start sm:flex-row sm:space-x-6 w-full sm:w-2/3">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-gray-100 mb-4 sm:mb-0">
              <img
                src={dj?.profile_img_url_1 || 'https://via.placeholder.com/300'}
                alt="Profile"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {dj?.stage_name || 'DJ Name'}
              </h2>
              <p className="text-gray-600">
                {dj?.first_name || ''} {dj?.last_name || ''}
              </p>
            </div>
          </div>
          {!isEditing && (
            <div className="flex flex-col sm:w-1/3 space-y-2 mt-6 sm:mt-0 sm:pl-20">
              <button
                onClick={handleEdit}
                className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark
                focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Edit Profile
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const user = auth.currentUser;
                    if (user && user.email) {
                      await resetPassword(user.email);
                      setShowPasswordResetModal(true);
                    }
                  } catch (error) {
                    toast.error('Failed to send password reset email. Please try again.');
                    console.error('Password reset error:', error);
                  }
                }}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Change Password
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Information */}
      <form
        id="djForm"
        className="space-y-6"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact and Basic Info */}
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              Contact & DJ Information
            </h3>
            <div className="space-y-4">
              {isEditing && (
                <p className="text-sm text-gray-500 italic">
                  Please use your legal name for this account, as it will be associated with any
                  payments made to you.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="First Name"
                  name="first_name"
                  value={dj.first_name}
                  disabled={!isEditing}
                  required={isEditing}
                />
                <InputField
                  label="Last Name"
                  name="last_name"
                  value={dj.last_name}
                  disabled={!isEditing}
                  required={isEditing}
                />
              </div>
              <InputField
                label="DJ Name"
                name="stage_name"
                value={dj.stage_name}
                disabled={!isEditing}
                required={isEditing}
              />
              <InputField
                label="Email"
                name="email_address"
                type="email"
                value={dj.email_address}
                disabled={!isEditing}
                required={isEditing}
              />
              <InputField
                label="Cell Phone"
                name="phone_number"
                value={dj.phone_number}
                disabled={!isEditing}
                required={isEditing}
              />
              {isEditing && (
                <p className="text-sm text-gray-500 mt-1 mb-4 italic">
                  This phone number will be used to process your payment after completing
                  your set(s). Please ensure this is your personal number and avoid using
                  VOIP numbers (e.g. Google Voice).
                </p>
              )}
              <InputField
                label="Website"
                name="website"
                type="url"
                value={dj.website}
                disabled={!isEditing}
                required={false}
              />

              {/* Organization Connection */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-2">Connection to Church, School, or Organization</h4>
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-1">
                        <input
                          type="radio"
                          name="has_org_connection"
                          value="Yes"
                          checked={dj.has_org_connection === "Yes"}
                          onChange={() => setDj({...dj, has_org_connection: "Yes"})}
                          disabled={!isEditing}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center space-x-1">
                        <input
                          type="radio"
                          name="has_org_connection"
                          value="No"
                          checked={dj.has_org_connection === "No" || !dj.has_org_connection}
                          onChange={() => {
                            setDj({...dj, has_org_connection: "No", org_connection_details: ""});
                          }}
                          disabled={!isEditing}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span>No</span>
                      </label>
                    </div>
                    
                    {dj.has_org_connection === "Yes" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Connection Details
                          <span className="text-xs text-gray-500 ml-1">
                            (e.g., "I'm a Deacon at XXX Church" or "I'm a coach at XXX High School")
                          </span>
                        </label>
                        <textarea
                          name="org_connection_details"
                          value={dj.org_connection_details || ""}
                          onChange={(e) => setDj({...dj, org_connection_details: e.target.value})}
                          className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows="3"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {dj.has_org_connection === "Yes" ? "Yes" : "No"}
                    </p>
                    {dj.has_org_connection === "Yes" && dj.org_connection_details && (
                      <p className="text-sm text-gray-700 mt-2">{dj.org_connection_details}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Addresses and Social */}
          <div className="space-y-6">
            {/* Addresses (copied logic from Onboarding) */}
            <div className="bg-white rounded-lg p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Addresses</h3>
              <div className="space-y-4">
                {isEditing ? (
                  <>
                    {/* Home Address — individual subcomponents in edit mode */}
                    <AddressFields
                      ref={homeRef}
                      label="Home Address"
                      value={home}
                      onChange={setHome}
                      required
                      verified={homeVerified}
                      onVerifiedChange={setHomeVerified}
                      onConfirmSave={handleSubmit}
                    />

                    {/* "Same Address" checkbox */}
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="sameAddressCheck"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={sameAddress}
                        onChange={(e) => {
                          setSameAddress(e.target.checked);
                          if (e.target.checked) {
                            setShipping(home);
                            setShippingVerified(homeVerified);
                          }
                        }}
                      />
                      <label
                        htmlFor="sameAddressCheck"
                        className="ml-2 text-sm text-gray-700"
                      >
                        My shipping address is the same as my home address.
                      </label>
                    </div>

                    {/* Shipping Address — hidden when it mirrors home */}
                    {!sameAddress && (
                      <AddressFields
                        ref={shippingRef}
                        label="Shipping Address"
                        value={shipping}
                        onChange={setShipping}
                        required
                        verified={shippingVerified}
                        onVerifiedChange={setShippingVerified}
                        onConfirmSave={handleSubmit}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Compacted single-line addresses in view mode */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Home Address</label>
                      <p className="mt-1 block w-full rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {dj.home_address || composeAddress(home) || '—'}
                      </p>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Address</label>
                      <p className="mt-1 block w-full rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {dj.shipping_address || composeAddress(shipping) || '—'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-lg p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Social Media</h3>
              <div className="space-y-4">
                {/* Instagram */}
                <div
                  className={`p-4 rounded-lg ${
                    !isEditing
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 ${
                        isEditing ? 'text-gray-600' : 'text-white'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    <div className="flex-grow">
                      {isEditing ? (
                        <>
                          <InputField
                            label="Instagram Handle"
                            name="instagram_handle"
                            value={dj.instagram_handle}
                            required={isEditing}
                          />
                          <InputField
                            label="Follower Count"
                            name="instagram_followers"
                            value={dj.instagram_followers}
                            type="number"
                            required={isEditing}
                          />
                        </>
                      ) : (
                        <div>
                          <span className="block font-medium">
                            @{dj.instagram_handle}
                          </span>
                          <span className="text-sm opacity-75">
                            {dj.instagram_followers} followers
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* TikTok */}
                <div
                  className={`p-4 rounded-lg ${
                    !isEditing ? 'bg-black text-white' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <svg
                      className={`w-5 h-5 ${
                        isEditing ? 'text-gray-600' : 'text-white'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                    </svg>
                    <div className="flex-grow">
                      {isEditing ? (
                        <>
                          <InputField
                            label="TikTok Handle"
                            name="tiktok_handle"
                            value={dj.tiktok_handle}
                            required={isEditing}
                          />
                          <InputField
                            label="Follower Count"
                            name="tiktok_followers"
                            value={dj.tiktok_followers}
                            type="number"
                            required={isEditing}
                          />
                        </>
                      ) : (
                        <div>
                          <span className="block font-medium">
                            @{dj.tiktok_handle}
                          </span>
                          <span className="text-sm opacity-75">
                            {dj.tiktok_followers} followers
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Facebook (NOT required) */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.515c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <div className="flex-grow">
                      {isEditing ? (
                        <>
                          <InputField
                            label="Facebook Profile"
                            name="facebook_handle"
                            value={dj.facebook_handle}
                            required={false}
                            placeholder="Enter your Facebook profile URL"
                          />
                          <InputField
                            label="Follower Count"
                            name="facebook_followers"
                            value={dj.facebook_followers}
                            type="number"
                            required={false}
                          />
                        </>
                      ) : (
                        <div>
                          <span className="block text-sm font-medium text-gray-900">
                            {dj.facebook_handle
                              ? '@' + dj.facebook_handle
                              : 'Facebook'}
                          </span>
                          <span className="block text-sm text-gray-500">
                            {dj.facebook_followers
                              ? `${dj.facebook_followers} followers`
                              : 'Not provided'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* X (Twitter) - NOT required */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    <div className="flex-grow">
                      {isEditing ? (
                        <>
                          <InputField
                            label="X (Twitter) Handle"
                            name="twitter_handle"
                            value={dj.twitter_handle}
                            required={false}
                            placeholder="Enter your X (Twitter) handle"
                          />
                          <InputField
                            label="Follower Count"
                            name="twitter_followers"
                            value={dj.twitter_followers}
                            type="number"
                            required={false}
                          />
                        </>
                      ) : (
                        <div>
                          <span className="block text-sm font-medium text-gray-900">
                            {dj.twitter_handle
                              ? '@' + dj.twitter_handle
                              : 'X (Twitter)'}
                          </span>
                          <span className="block text-sm text-gray-500">
                            {dj.twitter_followers
                              ? `${dj.twitter_followers} followers`
                              : 'Not provided'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Twitch - NOT required */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                    </svg>
                    <div className="flex-grow">
                      {isEditing ? (
                        <>
                          <InputField
                            label="Twitch Handle"
                            name="twitch_handle"
                            value={dj.twitch_handle}
                            required={false}
                            placeholder="Enter your Twitch handle"
                          />
                          <InputField
                            label="Follower Count"
                            name="twitch_followers"
                            value={dj.twitch_followers}
                            type="number"
                            required={false}
                          />
                        </>
                      ) : (
                        <div>
                          <span className="block text-sm font-medium text-gray-900">
                            {dj.twitch_handle
                              ? '@' + dj.twitch_handle
                              : 'Twitch'}
                          </span>
                          <span className="block text-sm text-gray-500">
                            {dj.twitch_followers
                              ? `${dj.twitch_followers} followers`
                              : 'Not provided'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* End Social Blocks */}
              </div>
            </div>
          </div>
        </div>
        {/* If you want additional sections, place them here */}
      </form>
    </div>
  );
}
